import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import {
  StyledQuantityBox,
  StyledQuantityTag,
  StyledQuantitySubTag,
  StyledLoadingContainer,
} from './styled';

import { Link, Spacer, Flexer, Icon, Text, Modal, Button } from '@ontruck/orc';
import { FormatPrice } from 'utils/format';
import { Loading } from 'components/Loading';
import StyledFrozenTag from 'modules/shipper-order/Card/styled/StyledFrozenTag';
import { statusGroup, statusObject } from 'redux/modules/journeys/constants';
import { freezeJourney, unfreezeJourney } from 'modules/shipper-order/Card/http';
import { refreshJobDetail } from 'redux/modules/job-detail/effects';
import { useDispatch, useSelector } from 'react-redux';
import {
  ALLOW_FREEZE_JOBS,
  featurePreferenceToggle,
} from 'modules/preferences-manager/preferences';
import { constants, getSpecificClientConfig } from '../../../redux/modules/client-config/selectors';

const QuantityBox = ({
  intl,
  id,
  quantityType,
  allowEdit = false,
  currency,
  net_full_price,
  net_full_cost,
  gross_full_price,
  gross_full_cost,
  onClickEdit,
  loading,
  onlyBorderRight,
  mRight,
  is_frozen,
  openViewCost,
  status,
  showPriceWithVat = true,
}) => {
  const unfreezeEnabled = useSelector((state) =>
    getSpecificClientConfig(state, constants.MERCURY_UNFREEZE_ENABLED)
  );

  const t = intl.messages.modules.quantityBox;
  const net = quantityType === 'price' ? net_full_price : net_full_cost;
  const gross = quantityType === 'price' ? gross_full_price : gross_full_cost;
  const dispatch = useDispatch();
  const allowFreezejobs = featurePreferenceToggle(ALLOW_FREEZE_JOBS);
  const canFreeze = !is_frozen && statusObject[status] === statusGroup.FINISHED && allowFreezejobs;
  const canUnfreeze = is_frozen && unfreezeEnabled;
  const [freezeModalOpened, setFreezeModalOpened] = useState(false);
  const [unfreezeModalOpened, setUnfreezeModalOpened] = useState(false);
  const [freezeError, setFreezeError] = useState(false);
  const [unfreezeError, setUnfreezeError] = useState(false);
  const [isFreezing, setIsFreezing] = useState(false);
  const [isUnfreezing, setIsUnfreezing] = useState(false);

  const onFreezeModalCancel = useCallback(() => {
    setFreezeModalOpened(false);
  }, [setFreezeModalOpened]);

  const onUnfreezeModalCancel = useCallback(() => {
    setUnfreezeModalOpened(false);
  }, [setUnfreezeModalOpened]);

  const onFreezeOrderClick = useCallback(() => {
    setFreezeModalOpened(true);
  }, [setFreezeModalOpened]);

  const onUnfreezeOrderClick = useCallback(() => {
    setUnfreezeModalOpened(true);
  }, [setUnfreezeModalOpened]);

  const onFreezeModalAccept = useCallback(async () => {
    setIsFreezing(true);
    setFreezeError(false);
    const success = await freezeJourney(id);
    if (!success) {
      setFreezeError(true);
    } else {
      dispatch(refreshJobDetail({ journeyId: id }));
      setFreezeModalOpened(false);
    }
    setIsFreezing(false);
  }, [dispatch, id, setFreezeError, setFreezeModalOpened, setIsFreezing]);

  const onUnfreezeModalAccept = useCallback(async () => {
    setIsUnfreezing(true);
    setUnfreezeError(false);
    const success = await unfreezeJourney(id);
    if (!success) {
      setUnfreezeError(true);
    } else {
      dispatch(refreshJobDetail({ journeyId: id }));
      setUnfreezeModalOpened(false);
    }
    setIsUnfreezing(false);
  }, [dispatch, id, setUnfreezeError, setUnfreezeModalOpened, setIsUnfreezing]);

  if (!quantityType) {
    console.error('You need to specify quantityType');
    return null;
  }

  return (
    <StyledQuantityBox mRight={mRight} onlyBorderRight={onlyBorderRight} data-testid="quantity-box">
      <div>{t.title[quantityType]}</div>
      <StyledQuantityTag>
        <Flexer.Parent alignItems="center">
          <FormatPrice value={net} currency={currency} />
        </Flexer.Parent>
      </StyledQuantityTag>
      {showPriceWithVat && (
        <StyledQuantitySubTag>
          <FormatPrice value={gross} currency={currency} /> {t.vat_inc}.
        </StyledQuantitySubTag>
      )}
      {allowEdit && !is_frozen && (
        <Spacer mTop="S">
          <Link
            onClick={onClickEdit}
            text={t.change[quantityType]}
            weight="bold"
            dataE2E={`detail__change_${quantityType}`}
            data-testid="quantity-box__edit-button"
          />
        </Spacer>
      )}
      {is_frozen && quantityType === 'cost' && (
        <>
          <Spacer mTop="S" data-testid="quantity-box__frozen-tag">
            <StyledFrozenTag>
              <Icon glyph="frozen" size="1.4rem" />
              &nbsp;
              <Text weight="bold">{t.frozen}</Text>
            </StyledFrozenTag>
          </Spacer>
        </>
      )}
      {is_frozen && (
        <Spacer mTop="S">
          <Link onClick={openViewCost} text={t.price_detail} weight="bold" />
        </Spacer>
      )}
      {canUnfreeze && (
        <Spacer mTop="S">
          <Link
            text={t.unfreeze_order}
            weight="bold"
            onClick={onUnfreezeOrderClick}
            data-testid="quantity-box__unfrozen-button"
          />
        </Spacer>
      )}
      {canFreeze && quantityType === 'cost' && (
        <Spacer mTop="S">
          <Link
            text={t.freeze_order}
            weight="bold"
            onClick={onFreezeOrderClick}
            data-testid="quantity-box__frozen-button"
          />
        </Spacer>
      )}
      {loading && (
        <StyledLoadingContainer>
          <Loading />
        </StyledLoadingContainer>
      )}
      <Modal
        show={freezeModalOpened}
        headerComponent={t.freeze_order}
        size="S"
        onClose={onFreezeModalCancel}
      >
        {t.freeze_order_message}
        {freezeError && (
          <Flexer.Parent mTop="S">
            <Text color="error500" text={t.freeze_error} data-testid="quantity-box__freeze-error" />
          </Flexer.Parent>
        )}
        {!isFreezing && (
          <>
            <Spacer mTop="M" mBottom="S">
              <Button
                fullWidth
                onClick={onFreezeModalAccept}
                data-testid="quantity-box__confirm-frozen"
              >
                {t.freeze}
              </Button>
            </Spacer>
            <Button
              fullWidth
              nude
              skin="secondary"
              onClick={onFreezeModalCancel}
              data-testid="quantity-box__cancel-frozen"
            >
              {t.cancel}
            </Button>
          </>
        )}
        {isFreezing && (
          <Flexer.Parent mTop="S" justifyContent="center">
            <Loading />
          </Flexer.Parent>
        )}
      </Modal>
      <Modal
        show={unfreezeModalOpened}
        headerComponent={t.unfreeze_order}
        size="S"
        onClose={onUnfreezeModalCancel}
      >
        {t.unfreeze_order_message}
        {unfreezeError && (
          <Flexer.Parent mTop="S">
            <Text
              color="error500"
              text={t.unfreeze_error}
              data-testid="quantity-box__unfreeze-error"
            />
          </Flexer.Parent>
        )}
        {!isUnfreezing && (
          <>
            <Spacer mTop="M" mBottom="S">
              <Button
                fullWidth
                onClick={onUnfreezeModalAccept}
                data-testid="quantity-box__confirm-unfrozen"
              >
                {t.unfreeze}
              </Button>
            </Spacer>
            <Button
              fullWidth
              nude
              skin="secondary"
              onClick={onUnfreezeModalCancel}
              data-testid="quantity-box__cancel-frozen"
            >
              {t.cancel}
            </Button>
          </>
        )}
        {isUnfreezing && (
          <Flexer.Parent mTop="S" justifyContent="center">
            <Loading />
          </Flexer.Parent>
        )}
      </Modal>
    </StyledQuantityBox>
  );
};

QuantityBox.propTypes = {
  quantityType: PropTypes.oneOf(['price', 'cost']),
  currency: PropTypes.string,
  allowEdit: PropTypes.bool,
  onClickEdit: PropTypes.func,
  loading: PropTypes.bool,
  net_full_price: PropTypes.string,
  net_full_cost: PropTypes.string,
  gross_full_price: PropTypes.string,
  gross_full_cost: PropTypes.string,
  onlyBorderRight: PropTypes.bool,
  mRight: PropTypes.string,
  is_frozen: PropTypes.bool,
  openViewCost: PropTypes.func,
};

export default injectIntl(QuantityBox);
